import type { ReactNode } from 'react';
import React, { useCallback, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import useOutsideClick from '../../../../../hooks/useOutsideClick';
import { getDeepChildrenOfType } from '../../../../../utils/getChildrenOfType';
import List from '../../../../List/List';
import Tooltip from '../../../../Tooltip/Tooltip';
import {
  ButtonWrapper,
  ListWrapper,
  StyledOtherVertIcon,
  Wrapper,
} from './ContextualMenu.styled';

export interface ContextualMenuProps {
  /**
   * Property used for testing purposes
   */
  dataCy?: string;
  children?: ReactNode[];
  /**
   * Indicates the number of items visible in the menu. If there are more, a scroll will appear
   */
  visibleListItems?: number;
  /**
   * Text displayed in the ContextualMenu tooltip
   */
  tooltipText: string;
  /**
   * DataCy for the list
   */
  listDataCy?: string;
  /**
   * Indicates whether the list positioning logic should listen to the scroll event.
   * By default it always listens to it. More information in [Popper.js](https://popper.js.org/docs/v2/modifiers/event-listeners/)
   */
  scrollListener?: boolean;
  /**
   * Function that is executed when the list is displayed
   */
  onShowList?: () => void;
}

export const ContextualMenu = ({
  dataCy,
  children,
  visibleListItems,
  tooltipText,
  listDataCy,
  scrollListener = false,
  onShowList,
}: ContextualMenuProps) => {
  const arrayOfChildren = React.Children.toArray(children);
  const [showList, setShowList] = useState(false);
  const theme = useTheme();

  const buttonWrapperRef = useRef<HTMLDivElement>(null);

  const ref = useOutsideClick(() => setShowList(false));

  const listItems =
    children &&
    getDeepChildrenOfType(arrayOfChildren, [List.Item, List.Divider, Tooltip]);

  const toggleShowList = useCallback(() => {
    setShowList(!showList);

    if (!showList) {
      onShowList?.();
    }
  }, [onShowList, showList]);

  const handleClick = toggleShowList;

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        toggleShowList();
      }
    },
    [toggleShowList],
  );

  const handleHideList = () => {
    setShowList(false);
  };

  return (
    <Wrapper ref={ref}>
      <Tooltip text={tooltipText}>
        <ButtonWrapper
          ref={buttonWrapperRef}
          data-cy={dataCy}
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        >
          <StyledOtherVertIcon />
        </ButtonWrapper>
      </Tooltip>
      <ListWrapper onClick={handleHideList}>
        <List
          dataCy={listDataCy}
          show={showList}
          width="auto"
          placement={List.Placement.BOTTOM_END}
          fallbackPlacements={[
            List.Placement.BOTTOM_START,
            List.Placement.TOP_START,
            List.Placement.TOP_END,
          ]}
          visibleItems={visibleListItems}
          referenceElement={buttonWrapperRef}
          scrollListener={scrollListener}
          style={{
            minWidth: theme.size.units(26),
            maxWidth: theme.size.units(50),
          }}
        >
          {listItems}
        </List>
      </ListWrapper>
    </Wrapper>
  );
};

ContextualMenu.Item = List.Item;
ContextualMenu.Divider = List.Divider;
